/* eslint-disable max-lines-per-function */
import {
  type Layer,
  type StandardChartLayerSpec,
  defineStandardChartLayer
} from '../../../core/layer';
import DEFINE from '../../../util/DEFINE';
import { setNameTextStyle } from '../../../helper/standardChart';

const visInfo: Layer['visInfo'] = {
  id: 'rankingLine',
  name_en: 'Ranking Line',
  // name_zh: '排名折线图',
  types_en: [],
  purpose_en: [],
  purpose_zh: [],
  standers: '',
  rules: {
    x: amount => amount > 2,
    y: amount => amount > 1,
    z: amount => amount > 3,
    data: amount => amount > 1
  },
  /** 保证和 Spec.encoding 字段一致 */
  parameters: {
    x: {
      itemType: [DEFINE.DATE],
      category: DEFINE.SINGLE
    },
    y: {
      itemType: [DEFINE.NUMBER, DEFINE.STR],
      category: DEFINE.SINGLE
    },
    z: { itemType: [DEFINE.STR], category: DEFINE.SINGLE }
  }
};

export interface Spec extends StandardChartLayerSpec {
  encoding: {
    x: string;
    y: string;
    z: string;
  };
}

export const layer = defineStandardChartLayer<Spec>({
  type: visInfo.id,
  isIndependentLayer: true,
  visInfo,
  render(api, spec, $dom) {
    const { data: dataArray, util, hook } = api;
    const {
      dataIndex = 0,
      encoding: { x, y, z }
    } = spec;
    const data = dataArray[dataIndex];
    const dataMetaInfo = hook.parseDataMetaInfo(data);
    const presetOption = hook.buildStandardChartPresetOption(api);

    // * ------------------------- data
    const seriesDataByName = {};

    const transformDate = util.dataTransform.transform(data.values.slice(), [
      { type: 'sort', field: x, order: 'ascending' }
    ]);
    const { xAxisData, seriesNames } = util.dataEncoding.encoding(transformDate, {
      xAxisData: {
        field: x,
        transforms: [{ type: 'deDuplication' }, { type: 'sort', order: 'ascending' }]
      },
      seriesNames: { field: z, transforms: [{ type: 'deDuplication' }] }
    });

    // * ------------------------- chart
    seriesNames.forEach(seriesName => {
      const seriesData = util.dataTransform.transform(transformDate.slice(), [
        { type: 'filter', cb: item => item[z] === seriesName }
      ]);

      seriesDataByName[seriesName] = [];

      xAxisData.forEach(date => {
        const dataItem = util.dataTransform.transform(seriesData.slice(), [
          { type: 'find', cb: item => item[x] === date }
        ]);
        seriesDataByName[seriesName].push({ __origin: dataItem });
      });
    });

    const seriesNamesTemp = new Set();

    // 对字段进行排序
    xAxisData.forEach((date, index) => {
      const columns = [];
      seriesNames.forEach(seriesName => {
        const dataItem = util.dataTransform.transform(seriesDataByName[seriesName].slice(), [
          { type: 'find', cb: item => item?.__origin?.[x] === date }
        ]);
        if (dataItem) {
          columns.push(dataItem);
        }
      });

      columns.sort((a, b) => Number(b.__origin[y]) - Number(a.__origin[y]));

      columns.forEach((item, idx) => {
        // 排名结果
        // item.value = [index, idx];
        item.value = String(idx + 1);

        // 收集第一列数据的排名信息
        seriesNamesTemp.add(item.__origin[z]);
      });
    });

    const newSeriesNames = Array.from(seriesNamesTemp);

    const getSeries = (name, seriesData, xData) => {
      return {
        clip: true,
        withTimeline: {
          range: [0, xData.length - 1],
          curIndex: xData.length - 1,
          maxRange: xData.length - 1
        },
        name,
        type: 'dvRankLine',
        data: seriesData,
        lineStyle: {
          width: 1
        },
        emphasis: {
          focus: 'series',
          itemStyle: {
            borderColor: '#3b3b3b',
            borderWidth: 2
          }
        },
        symbol: 'circle',
        labelLayout: {
          labelOverlap: 'shiftY'
        }
      };
    };

    // 渲染图表
    const option = {
      grid: {
        dvBoxLayout: null,
        top: 14
      },
      tooltip: {
        formatter: params_ => {
          const series = Array.isArray(params_) ? params_ : [params_];
          const items = [];

          series.forEach(item => {
            const result = hook.formatDataNumberValue(item.value, {
              type: 'table',
              unit: dataMetaInfo[item.seriesName]?.unit
            });

            items.push({
              type: util.const.SERIES_2_LEGEND_TYPE_MAP.line,
              color: item.color,
              name: item.seriesName,
              value: result.value + result.unit
            });
          });

          return hook.buildTooltip({
            config: hook.buildStandardChartPresetOption(api).tooltip,
            title: series[0]?.name,
            items
          });
        }
      },
      xAxis: [
        {
          show: true,
          type: 'category',
          position: 'top',
          name: x + (dataMetaInfo[x]?.unit ? `(${dataMetaInfo[x].unit})` : ''),
          data: xAxisData,
          boundaryGap: false,
          axisLine: {
            show: false
          },
          axisTick: {
            show: false
          },
          splitLine: {
            show: false,
            interval: 0
          },
          axisLabel: {
            dvAlignEdge: true
          }
        }
      ],
      yAxis: [
        {
          show: true,
          type: 'category',
          // name: '排名',
          inverse: true,
          axisLabel: {
            margin: 15
          },
          boundaryGap: false,
          axisTick: {
            show: false,
            alignWithLabel: false
          },
          axisLine: {
            show: false
          },
          splitLine: {
            show: false,
            interval: 0,
            lineStyle: {
              opacity: 0.5
            }
          },
          data: Array.from(Array(newSeriesNames.length), (_, index) => String(index + 1))
        }
      ],
      series: newSeriesNames.map(name => {
        return getSeries(name, seriesDataByName[name], xAxisData);
      })
    };
    setNameTextStyle($dom, option);
    return option;
  }
});
